import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useState, useRef, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { setLogOut } from "../../store/authSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faTimes, faHome, faUsers, faUserFriends, faFileAlt, faGavel, faCog, faBell, faSignOutAlt, faUser, faChartBar, faMoneyBill, faFlag, faChevronDown, faVideo } from "@fortawesome/free-solid-svg-icons";
import ErrorBoundary from "../../components/errorboundary/errorboundary";
import Logo from "../../components/logo/Logo";
import AuthService from "../../services/authService";
import NotificationService from "../../services/notificationService";

const PARTICIPANT_NAV = [
  { to: '/dashboard', icon: faHome, label: 'Dashboard' },
  { to: '/team', icon: faUsers, label: 'My Team' },
  { to: '/submission/new', icon: faFileAlt, label: 'Submission' },
  { to: '/video-pitch', icon: faVideo, label: 'Video Pitch' },
  { to: '/fundraising', icon: faMoneyBill, label: 'Fundraising' },
  { to: '/implementation', icon: faFlag, label: 'Implementation' },
];

const JUDGE_NAV = [
  { to: '/judge/dashboard', icon: faHome, label: 'Dashboard' },
  { to: '/judge/calibration', icon: faGavel, label: 'Calibration' },
];

const ADMIN_NAV = [
  { to: '/admin/dashboard', icon: faChartBar, label: 'Dashboard' },
  { to: '/admin/competition', icon: faCog, label: 'Competition' },
  { to: '/admin/submissions', icon: faFileAlt, label: 'Submissions' },
  { to: '/admin/teams', icon: faUserFriends, label: 'Teams' },
  { to: '/admin/judges', icon: faGavel, label: 'Judges' },
  { to: '/admin/fundraising', icon: faMoneyBill, label: 'Fundraising' },
  { to: '/admin/implementation', icon: faFlag, label: 'Implementation' },
  { to: '/admin/users', icon: faUsers, label: 'Users' },
  { to: '/admin/notifications', icon: faBell, label: 'Notifications' },
];

export default function DashboardLayout() {
  const [sidebar, setSidebar] = useState(false);
  const [profileMenu, setProfileMenu] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    NotificationService.getUnreadCount()
      .then((res) => { if (!cancelled) setUnreadCount(res.data?.data?.count || 0); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [location.pathname]);

  const navItems = user?.role === 'admin' ? ADMIN_NAV
    : user?.role === 'judge' ? JUDGE_NAV
    : PARTICIPANT_NAV;

  const handleLogout = async () => {
    try {
      await AuthService.logout();
    } catch { /* still log out locally even if API fails */ }
    dispatch(setLogOut());
    Sentry.setUser(null);
    navigate('/login');
  };

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileMenu && !e.target.closest('[data-profile-menu]')) {
        setProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileMenu]);

  return (
    <div className="min-h-screen flex flex-col bg-grey-bg">
      {/* Header */}
      <header className="fixed top-0 right-0 w-full bg-white z-20 border-b border-grey-200">
        <div className="flex items-center justify-between h-16 px-4 md:px-6">
          <div className="flex items-center gap-4">
            <button
              className="md:hidden cursor-pointer"
              onClick={() => setSidebar(!sidebar)}
            >
              <FontAwesomeIcon icon={sidebar ? faTimes : faBars} />
            </button>
            <Logo to="/dashboard" className="h-8" />
          </div>
          <div className="flex items-center gap-4">
            <NavLink to="/notifications" className="relative text-grey-667 hover:text-primary">
              <FontAwesomeIcon icon={faBell} />
              {unreadCount > 0 && (
                <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </NavLink>
            <div className="relative" data-profile-menu>
              <button
                onClick={() => setProfileMenu(!profileMenu)}
                className="flex items-center gap-2 text-sm text-grey-667 hover:text-primary cursor-pointer"
              >
                <div className="w-8 h-8 bg-primary-bg rounded-full flex items-center justify-center text-xs font-bold text-primary">
                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                </div>
                <span className="hidden md:inline">{user?.firstName}</span>
                <FontAwesomeIcon icon={faChevronDown} className="text-xs hidden md:inline" />
              </button>
              {profileMenu && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-grey-200 py-2 z-30">
                  <div className="px-4 py-2 border-b border-grey-200">
                    <p className="text-sm font-semibold text-grey-dark">{user?.firstName} {user?.lastName}</p>
                    <p className="text-xs text-grey-98 truncate">{user?.email}</p>
                  </div>
                  <NavLink
                    to="/profile"
                    onClick={() => setProfileMenu(false)}
                    className="flex items-center gap-3 px-4 py-2 text-sm text-grey-667 hover:bg-grey-bg"
                  >
                    <FontAwesomeIcon icon={faUser} className="w-4" />
                    My Profile
                  </NavLink>
                  <button
                    onClick={() => { setProfileMenu(false); handleLogout(); }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-500 hover:bg-red-50 cursor-pointer"
                  >
                    <FontAwesomeIcon icon={faSignOutAlt} className="w-4" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile sidebar overlay */}
      {sidebar && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebar(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-screen md:h-[calc(100vh-4rem)] w-[250px] bg-white z-50 transform transition-transform duration-300 ease-in-out md:translate-x-0 md:top-16 md:z-10 border-r border-grey-200 ${
        sidebar ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <nav className="flex flex-col h-full py-4">
          <div className="flex-1 space-y-1 px-3">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setSidebar(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    isActive
                      ? 'bg-primary-bg text-primary font-semibold'
                      : 'text-grey-667 hover:bg-grey-bg hover:text-grey-dark'
                  }`
                }
              >
                <FontAwesomeIcon icon={item.icon} className="w-4" />
                {item.label}
              </NavLink>
            ))}
          </div>
          <div className="px-3 mt-auto">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-red-500 hover:bg-red-50 w-full cursor-pointer"
            >
              <FontAwesomeIcon icon={faSignOutAlt} className="w-4" />
              Logout
            </button>
          </div>
        </nav>
      </aside>

      {/* Main content */}
      <main className="mt-16 md:ml-[250px] min-h-[calc(100vh-4rem)]">
        <div className="p-4 md:p-6">
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>
    </div>
  );
}
